import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useOrganization } from "../context/OrganizationContext";
import {
  extractSubscriptionList,
  getSubscriptionPlanId,
  getSubscriptionPlanName,
} from "../utils/subscription";
import apiService from "../services/apiService";
import stripeService from "../services/stripeService";
import { mergeAnalyticsPayloads } from "../utils/mergeAnalytics";

const BAR_BLUE = "#49B7F8";
const BAR_SALMON = "#FF6B6B";

function formatCompactInt(n) {
  if (n == null || Number.isNaN(Number(n))) return "0";
  let num = Number(n);
  const sign = num < 0 ? "-" : "";
  num = Math.abs(num);
  if (num < 1000) return sign + String(Math.round(num));
  const tiers = [
    { d: 1e9, s: "B" },
    { d: 1e6, s: "M" },
    { d: 1e3, s: "K" },
  ];
  for (const { d, s } of tiers) {
    if (num >= d) {
      const x = num / d;
      const decimals = x >= 100 ? 0 : x >= 10 ? 1 : 2;
      return sign + String(Number(x.toFixed(decimals))) + s;
    }
  }
  return sign + String(Math.round(num));
}

function formatInt(n) {
  if (n == null || Number.isNaN(n)) return "0";
  return Number(n).toLocaleString();
}

function UsageBar({ ratio, color, compact }) {
  const pct = Math.min(100, Math.max(0, (ratio || 0) * 100));
  const h = compact ? "h-2" : "h-2";
  return (
    <div className={`${h} w-full overflow-hidden rounded-full bg-white/[0.08]`}>
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

function formatLimit(key, value) {
  if (value == null) return null;
  if (key === "rps") return `${Number(value).toLocaleString()} RPS`;
  if (key === "monthly" && typeof value === "number" && value >= 1e12) {
    return "Unlimited / month";
  }
  if (typeof value === "number") {
    const suffix = key === "daily" ? "day" : "month";
    return `${value.toLocaleString()} / ${suffix}`;
  }
  return String(value);
}

// Formatters for the Extra Usage section. Kept inline so the component
// stays self-contained and matches the existing helpers' style.
const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function dollarsFromCents(cents) {
  return USD.format((Number(cents) || 0) / 100);
}

function formatResetDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export default function SubscriptionCurrentPlan({ onGoToPlans }) {
  const { subscription, refreshSubscription, currentOrganization } =
    useOrganization();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [usageLoading, setUsageLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  // ADR-0002 Phase 2: Extra Usage state. wallet === null means "not
  // loaded yet" or "404 — no wallet row yet"; we differentiate via
  // walletLoading so the UI doesn't render a spurious "no data" while
  // the request is in flight.
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletSaving, setWalletSaving] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [adjustDollars, setAdjustDollars] = useState("");
  const [buyDollars, setBuyDollars] = useState("");
  const [buySubmitting, setBuySubmitting] = useState(false);

  const loadUsage = useCallback(async () => {
    if (!currentOrganization?.id) {
      setAnalytics(null);
      setUsageLoading(false);
      return;
    }
    setUsageLoading(true);
    try {
      const rawProjects = await apiService.getProjects(currentOrganization.id);
      const projects = extractSubscriptionList(rawProjects);
      const keyLists = await Promise.all(
        (projects || []).map((p) =>
          apiService.getApiKeys(currentOrganization.id, p.uuid),
        ),
      );
      const keys = keyLists.flatMap((k) => extractSubscriptionList(k));
      if (!keys.length) {
        setAnalytics(null);
        return;
      }
      const results = await Promise.all(
        keys.map((k) => apiService.getApiKeyAnalytics(k.uuid, "30d")),
      );
      setAnalytics(mergeAnalyticsPayloads(results));
    } catch {
      toast.error("Failed to load usage");
      setAnalytics(null);
    } finally {
      setUsageLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!subscription) {
      setAnalytics(null);
      setUsageLoading(false);
      return undefined;
    }
    loadUsage();
    return undefined;
  }, [subscription?.id, loadUsage]);

  // Owner check — single source for both the existing plan-actions IIFE
  // and the new Extra Usage section. Owner email lives on
  // currentOrganization; userEmail comes from localStorage (same pattern
  // as the existing IIFE at the Cancel-subscription column).
  const isOwner = (() => {
    const ue = (localStorage.getItem("userEmail") || "").toLowerCase();
    return !!(
      currentOrganization?.owner_email &&
      currentOrganization.owner_email.toLowerCase() === ue
    );
  })();

  // Plan-level overage flag — drives whether the Extra Usage section
  // renders at all. The OVERAGE/CapExceeded badges below already key off
  // the per-period analytics flag (L?.overage_enabled). Both should agree
  // — the subscription serializer exposes plan_details.overage_enabled.
  const overageEnabled = !!subscription?.plan_details?.overage_enabled;
  const showExtraUsage = !!subscription && overageEnabled;

  // ADR-0002 Phase 2: load the wallet whenever the section is going to
  // render. We fetch regardless of ownership so non-owners still see the
  // section (with the toggle disabled + tooltip), but the backend
  // scrubs auto-recharge fields server-side for non-owners — that's why
  // we don't need to special-case the response shape here.
  const loadWallet = useCallback(async () => {
    if (!currentOrganization?.id || !showExtraUsage) {
      setWallet(null);
      setWalletLoading(false);
      return;
    }
    setWalletLoading(true);
    try {
      const data = await apiService.getCreditWallet(currentOrganization.id);
      setWallet(data || null);
    } catch (e) {
      // 404 is the documented "no wallet yet" shape — show zero state
      // but still render the section so the owner can opt in / top up.
      if (e?.response?.status === 404) {
        setWallet(null);
      } else {
        toast.error(e?.message || "Failed to load wallet");
      }
    } finally {
      setWalletLoading(false);
    }
  }, [currentOrganization?.id, showExtraUsage]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  // Stripe redirect-back picker. The backend's success_url drops us here
  // with ?topup=success — refetch the wallet to pick up the new balance,
  // then strip the query param so a refresh doesn't refetch unnecessarily.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const search = window.location?.hash?.includes("?")
      ? window.location.hash.split("?")[1]
      : window.location.search?.replace(/^\?/, "") || "";
    if (!search) return;
    const params = new URLSearchParams(search);
    if (params.get("topup") === "success") {
      toast.success("Top-up received — wallet updated.");
      loadWallet();
      // Strip the query param so a refresh doesn't re-toast.
      try {
        const hash = window.location.hash.split("?")[0];
        window.history.replaceState(null, "", `${window.location.pathname}${hash}`);
      } catch {
        // best-effort
      }
    } else if (params.get("topup") === "cancel") {
      // No toast — user explicitly cancelled. Just clean the URL.
      try {
        const hash = window.location.hash.split("?")[0];
        window.history.replaceState(null, "", `${window.location.pathname}${hash}`);
      } catch {
        // best-effort
      }
    }
    // Intentionally not depending on loadWallet — we only want to run
    // this on first mount (and after walletLoading flips false), not on
    // every loadWallet identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planName = getSubscriptionPlanName(subscription);
  const planId = getSubscriptionPlanId(subscription);
  const limits = subscription?.rate_limits || {};
  const priceUsd = subscription?.plan_details?.price_usd;

  const handleCancelAtPeriodEnd = async () => {
    if (!subscription?.id) return;
    setCanceling(true);
    try {
      await stripeService.cancelSubscription(subscription.uuid, true);
      toast.success(
        "Your subscription will end at the end of the current billing period. Until then you keep full access.",
      );
      setCancelOpen(false);
      await refreshSubscription();
    } catch (e) {
      toast.error(e?.message || "Could not cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

  const L = analytics?.limits;
  const monthlyLimit = L?.monthly_limit;
  const archiveLimit = L?.archive_limit;
  const archiveUsed = Number(L?.archive_used) || 0;
  const archiveRatio =
    archiveLimit && archiveLimit > 0
      ? Math.min(1, archiveUsed / archiveLimit)
      : 0;
  const archivePct =
    archiveLimit && archiveLimit > 0
      ? Math.round((archiveUsed / archiveLimit) * 100)
      : 0;
  const dailyLimit = L?.daily_limit;
  const totalReq = Number(analytics?.total_requests) || 0;
  const monthlyUsed = Number(L?.monthly_used) || 0;
  const dailyUsed = Number(L?.daily_used) || 0;
  // Raw full-bucket count — NOT CU-weighted. Distinct from monthlyUsed,
  // which is the weighted total that fills the plan's monthly cap.
  const fullUsed = Number(L?.full_used) || 0;
  const totalVsMonthlyRatio =
    monthlyLimit && monthlyLimit > 0 ? Math.min(1, totalReq / monthlyLimit) : 0;
  const dailyRatio =
    dailyLimit && dailyLimit > 0 ? Math.min(1, dailyUsed / dailyLimit) : 0;
  const monthlyRatio =
    monthlyLimit && monthlyLimit > 0
      ? Math.min(1, monthlyUsed / monthlyLimit)
      : 0;
  const monthlyPct =
    monthlyLimit && monthlyLimit > 0
      ? Math.round((monthlyUsed / monthlyLimit) * 100)
      : 0;
  const totalPct =
    monthlyLimit && monthlyLimit > 0
      ? Math.round((totalReq / monthlyLimit) * 100)
      : 0;
  // Overage state — see UsageDashboard.jsx for the full rationale.
  const monthlyOver = !!(monthlyLimit && monthlyUsed > monthlyLimit);
  const overAmount = monthlyOver ? monthlyUsed - monthlyLimit : 0;
  const overageBilling = monthlyOver && !!L?.overage_enabled;
  const usdPerMillionFull = L?.usd_per_million_full;

  const cardSurface =
    "rounded-sm border border-white/10 bg-landing-section p-4 sm:p-5";

  if (!subscription) {
    return (
      <div className="mx-auto w-full max-w-[400px]">
        <div className={cardSurface}>
          <h3 className="font-title text-base font-semibold text-white">
            Current plan
          </h3>
          <p className="font-secondary mt-2 text-sm leading-relaxed text-white/60">
            No active subscription for this organization. Choose a plan below.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full">
      <div className={cardSurface}>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-start divide-x divide-white/20 lg:gap-6">
          {/* Col 1: plan + actions — drives row height */}
          <div className="min-w-0 lg:col-span-3">
            <div className="flex flex-col gap-3">
              <div>
                <h3 className="font-title text-base font-semibold text-white">
                  Current plan
                </h3>
                <p className="font-title mt-1 text-xl font-bold leading-tight text-primary">
                  {planName || "—"}
                </p>
                <div className="font-secondary mt-2 flex flex-wrap items-center gap-1.5 text-xs text-white/70">
                  <span
                    className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold ${
                      subscription.active
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-white/10 text-white/60"
                    }`}
                  >
                    {subscription.stripe_status ||
                      (subscription.active ? "active" : "inactive")}
                  </span>
                  {subscription.cancel_at_period_end ? (
                    <span className="rounded-sm bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-200">
                      Canceling at period end
                    </span>
                  ) : null}
                  {priceUsd != null && priceUsd !== "" ? (
                    <span className="text-white/50">
                      ${Number(priceUsd).toFixed(2)}/mo
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {/* Owner-only: Update plan + Cancel subscription are
                    billing-write actions and the backend rejects them
                    for non-owners (403 owner_only). Hide for everyone
                    else and surface a single explanatory chip. */}
                {!isOwner ? (
                  <span
                    className="font-secondary inline-flex items-center justify-center rounded-sm border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/55"
                    title="Only the organization owner can change or cancel the plan."
                  >
                    Owner-only
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onGoToPlans?.()}
                      className="font-secondary w-full rounded-sm border border-white/15 bg-transparent px-3 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/[0.06]"
                    >
                      Update plan
                    </button>
                    {subscription.stripe_subscription_id ? (
                      <button
                        type="button"
                        onClick={() => setCancelOpen((o) => !o)}
                        className="font-secondary w-full rounded-sm border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/10"
                      >
                        Cancel subscription
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Col 2: plan limits */}
          <div className="min-w-0 pt-4 lg:col-span-3 lg:pl-6 lg:pt-0 h-full">
            <h4 className="font-secondary text-sm font-semibold uppercase tracking-wide text-white/45">
              Plan limits (included)
            </h4>
            <ul className="mt-2 space-y-1.5">
              {["rps", "daily", "monthly"].map((k) => {
                const label =
                  k === "rps" ? "RPS" : k === "daily" ? "Daily" : "Monthly";
                const v = limits[k];
                const text = formatLimit(k, v);
                if (!text) return null;
                return (
                  <li key={k} className="rounded-sm bg-black/20 px-2 py-1.5">
                    <p className="font-secondary text-xs text-white/45">
                      {label}
                    </p>
                    <p className="font-title text-sm font-semibold leading-tight text-white">
                      {text}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Col 3: usage — compact to align with plan column height */}
          <div className="min-w-0 h-full pt-4 lg:col-span-6 lg:pl-6 lg:pt-0">
            <h4 className="font-secondary text-sm font-semibold uppercase tracking-wide text-white/45">
              Usage vs plan (30d)
            </h4>
            {usageLoading ? (
              <div className="mt-2 flex justify-center py-4">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : !analytics?.limits ? (
              <p className="font-secondary mt-2 text-xs leading-snug text-white/45">
                No usage data yet. Create an API key and send traffic to see
                metrics.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-secondary text-xs text-white/55">
                      Total (period)
                    </span>
                    {monthlyLimit ? (
                      <span className="font-title text-xs font-semibold text-primary">
                        {totalPct}%
                      </span>
                    ) : null}
                  </div>
                  <p className="font-title text-sm font-semibold text-white">
                    {formatCompactInt(totalReq)}
                    {monthlyLimit ? (
                      <span className="font-secondary font-normal text-white/40">
                        {" "}
                        / {formatCompactInt(monthlyLimit)}
                      </span>
                    ) : null}
                  </p>
                  {monthlyLimit ? (
                    <UsageBar
                      compact
                      ratio={totalVsMonthlyRatio}
                      color={BAR_BLUE}
                    />
                  ) : (
                    <p className="font-secondary text-xs text-white/30">
                      No monthly cap in API
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-secondary text-xs text-white/55">
                        Daily
                      </span>
                      {dailyLimit != null ? (
                        <span
                          className="font-title text-xs font-semibold"
                          style={{ color: BAR_SALMON }}
                        >
                          {formatInt(L?.daily_remaining ?? 0)} left
                        </span>
                      ) : null}
                    </div>
                    <p className="font-title mt-2 text-sm text-white">
                      {formatCompactInt(dailyUsed)}
                    </p>
                    {dailyLimit ? (
                      <div className="mt-3">
                        <UsageBar
                          compact
                          ratio={dailyRatio}
                          color={BAR_SALMON}
                        />
                      </div>
                    ) : (
                      <div
                        className="mt-3 h-2 w-full rounded-full"
                        style={{ backgroundColor: "rgba(255, 107, 107, 0.14)" }}
                      />
                    )}
                  </div>
                  {/* Monthly usage — CU-weighted total against the plan's
                      monthly cap. Archive calls feed this counter at the
                      plan's archive_weight multiplier (Free=6×), so this
                      is the weighted view the gateway actually enforces.
                      Distinct from raw "Full node" (raw count) below. */}
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-secondary text-xs text-white/55 flex items-center gap-1.5">
                        Monthly usage
                        {overageBilling ? (
                          <span className="rounded-sm border border-amber-400/40 bg-amber-400/10 px-1 py-0.5 font-title text-[9px] font-semibold uppercase tracking-wider text-amber-300">
                            Overage
                          </span>
                        ) : monthlyOver ? (
                          <span className="rounded-sm border border-red-400/40 bg-red-400/10 px-1 py-0.5 font-title text-[9px] font-semibold uppercase tracking-wider text-red-300">
                            Cap exceeded
                          </span>
                        ) : null}
                      </span>
                      {monthlyLimit ? (
                        <span
                          className={`font-title text-xs font-semibold ${
                            monthlyOver
                              ? overageBilling
                                ? "text-amber-300"
                                : "text-red-300"
                              : "text-primary"
                          }`}
                        >
                          {monthlyPct}%
                        </span>
                      ) : null}
                    </div>
                    <p className="font-title mt-2 text-sm text-white">
                      {formatCompactInt(monthlyUsed)}
                      {monthlyLimit ? (
                        <span className="text-white/40 text-xs font-secondary font-medium ml-1">
                          / {formatCompactInt(monthlyLimit)}
                        </span>
                      ) : null}
                    </p>
                    {monthlyLimit ? (
                      <div className="mt-3">
                        <UsageBar
                          compact
                          ratio={monthlyRatio}
                          color={BAR_BLUE}
                        />
                      </div>
                    ) : (
                      <div
                        className="mt-3 h-2 w-full rounded-full"
                        style={{ backgroundColor: "rgba(73, 183, 248, 0.14)" }}
                      />
                    )}
                    {overageBilling ? (
                      <p className="font-secondary mt-2 text-[11px] text-amber-300/90">
                        {formatCompactInt(overAmount)} over
                        {usdPerMillionFull != null ? (
                          <> · ${usdPerMillionFull}/M billed</>
                        ) : null}
                      </p>
                    ) : monthlyOver ? (
                      <p className="font-secondary mt-2 text-[11px] text-red-300/90">
                        Cap exceeded — requests blocked
                      </p>
                    ) : null}
                  </div>
                  {/* Full node requests — RAW count of full-bucket calls
                      this month, from Redis usage:bucket:full. No bar
                      (no cap of its own; the plan's monthly cap is the
                      cap). Spans both columns so the layout reads
                      Daily | Monthly, then Full (full-width), then
                      Archive (full-width). */}
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3 sm:col-span-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-secondary text-xs text-white/55">
                        Full node
                        <span className="ml-2 text-[10px] text-white/35 font-normal">
                          this month, raw count
                        </span>
                      </span>
                    </div>
                    <p className="font-title mt-2 text-sm text-white">
                      {formatCompactInt(fullUsed)}
                      <span className="text-white/40 text-xs font-secondary font-medium ml-1">
                        requests
                      </span>
                    </p>
                  </div>
                  {/* Archive bucket — render only when the plan has an
                      archive cap. Morado-purple to match the "expensive
                      / special" palette used in the docs page. */}
                  {archiveLimit ? (
                    <div className="rounded-sm border border-white/10 bg-black/20 p-3 sm:col-span-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-secondary text-xs text-white/55">
                          Archive node
                          <span className="ml-2 text-[10px] text-white/35 font-normal">
                            historical state queries
                          </span>
                        </span>
                        <span className="font-title text-xs font-semibold" style={{ color: "#a5a0e6" }}>
                          {archivePct}%
                        </span>
                      </div>
                      <p className="font-title mt-2 text-sm text-white">
                        {formatCompactInt(archiveUsed)}
                        <span className="text-white/40 text-xs font-secondary font-medium ml-1">
                          / {formatCompactInt(archiveLimit)}
                        </span>
                      </p>
                      <div className="mt-3">
                        <UsageBar
                          compact
                          ratio={archiveRatio}
                          color="#7367f0"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>

        {showExtraUsage ? (
          <ExtraUsageSection
            wallet={wallet}
            walletLoading={walletLoading}
            isOwner={isOwner}
            saving={walletSaving}
            onToggleExtraUsage={async (next) => {
              if (!isOwner) return;
              if (!currentOrganization?.id) return;
              // Optimistic update — rollback on error. Mirrors the
              // pattern in Settings.jsx for member-role flips. The next
              // natural wallet fetch (or the PATCH response) will
              // reconcile any drift; no manual refetch needed.
              const prev = wallet;
              setWallet((w) => ({ ...(w || {}), extra_usage_enabled: next }));
              setWalletSaving(true);
              try {
                const updated = await apiService.patchCreditWallet(
                  currentOrganization.id,
                  { extra_usage_enabled: next },
                );
                setWallet(updated || null);
              } catch (e) {
                setWallet(prev);
                toast.error(e?.message || "Failed to update extra usage");
              } finally {
                setWalletSaving(false);
              }
            }}
            onOpenAdjust={() => {
              if (!isOwner) return;
              const cents = Number(wallet?.monthly_spend_cap_cents) || 0;
              setAdjustDollars(String(Math.max(5, Math.round(cents / 100))));
              setAdjustOpen(true);
            }}
            onOpenBuy={() => {
              if (!isOwner) return;
              setBuyDollars("25");
              setBuyOpen(true);
            }}
          />
        ) : null}

        {adjustOpen ? (
          <SmallModal
            title="Adjust monthly spend limit"
            inputLabel="Monthly cap (USD)"
            min={5}
            max={1000}
            value={adjustDollars}
            onChange={setAdjustDollars}
            submitLabel={walletSaving ? "Saving…" : "Save"}
            disabled={walletSaving}
            onCancel={() => setAdjustOpen(false)}
            onSubmit={async () => {
              const dollars = Math.floor(Number(adjustDollars));
              if (!Number.isFinite(dollars) || dollars < 5 || dollars > 1000) {
                toast.error("Enter a whole dollar amount between $5 and $1000");
                return;
              }
              setWalletSaving(true);
              try {
                const updated = await apiService.patchCreditWallet(
                  currentOrganization.id,
                  { monthly_spend_cap_cents: dollars * 100 },
                );
                setWallet(updated || null);
                setAdjustOpen(false);
                toast.success("Monthly cap updated");
              } catch (e) {
                toast.error(e?.message || "Failed to update cap");
              } finally {
                setWalletSaving(false);
              }
            }}
          />
        ) : null}

        {buyOpen ? (
          <SmallModal
            title="Buy Extra Usage"
            inputLabel="Amount (USD)"
            min={5}
            max={500}
            value={buyDollars}
            onChange={setBuyDollars}
            submitLabel={buySubmitting ? "Redirecting…" : "Buy now"}
            disabled={buySubmitting}
            onCancel={() => setBuyOpen(false)}
            onSubmit={async () => {
              const dollars = Math.floor(Number(buyDollars));
              if (!Number.isFinite(dollars) || dollars < 5 || dollars > 500) {
                toast.error("Enter a whole dollar amount between $5 and $500");
                return;
              }
              setBuySubmitting(true);
              try {
                const resp = await apiService.createCreditTopup(
                  currentOrganization.id,
                  dollars * 100,
                );
                if (resp?.url) {
                  window.location.assign(resp.url);
                  return;
                }
                toast.error("Stripe did not return a checkout URL");
              } catch (e) {
                toast.error(e?.message || "Failed to start checkout");
              } finally {
                setBuySubmitting(false);
              }
            }}
          />
        ) : null}

        {cancelOpen ? (
          <div className="mt-4 border-t border-white/10 pt-4">
            <div className="rounded-sm border border-white/10 bg-black/30 p-3">
              <p className="font-secondary text-xs leading-relaxed text-white/75">
                We&apos;ll stop renewal after the current billing period ends.
                You keep your plan and limits until that date (same as Stripe
                &quot;cancel at period end&quot;).
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  disabled={canceling}
                  onClick={() => handleCancelAtPeriodEnd()}
                  className="font-secondary w-full rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-black transition-colors hover:bg-primary-hover disabled:opacity-50 sm:w-auto"
                >
                  Confirm — end after current period
                </button>
                <button
                  type="button"
                  onClick={() => setCancelOpen(false)}
                  className="font-secondary py-2 text-center text-sm text-white/60 hover:text-white sm:px-3"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Extra Usage section — ADR-0002 Phase 2 (image #9).                 */
/* ------------------------------------------------------------------ */

function ExtraUsageSection({
  wallet,
  walletLoading,
  isOwner,
  saving,
  onToggleExtraUsage,
  onOpenAdjust,
  onOpenBuy,
}) {
  // Zero-state when wallet === null (never topped up or 404). The
  // section still renders so the owner can engage; non-owners see the
  // toggle disabled with a tooltip.
  const capCents = Number(wallet?.monthly_spend_cap_cents) || 10000; // $100 default mirrors the model default
  const spentCents = Number(wallet?.period_spent_cents) || 0;
  const balanceCents = Number(wallet?.balance_cents) || 0;
  // ADR-0002 Phase 2: how many full-node vs archival requests the prepaid
  // wallet absorbed this month (distinct from the plan's own Full/Archive
  // cards above). Backed by the gateway's wallet_req Redis counters.
  const walletFullReq = Number(wallet?.period_full_requests) || 0;
  const walletArchiveReq = Number(wallet?.period_archive_requests) || 0;
  // ADR-0002 Phase 2: bind to the new `extra_usage_enabled` master switch
  // — distinct from `auto_recharge_enabled`, which is a deferred
  // sub-feature (saved-card off-session top-up). The two flags are
  // independent on the model so the UI can land before auto-recharge.
  const enabled = !!wallet?.extra_usage_enabled;
  const pct =
    capCents > 0
      ? Math.min(100, Math.round((spentCents / capCents) * 100))
      : 0;
  const resetsAt = wallet?.period_resets_at;
  // When the master toggle is on but the wallet is empty, the owner
  // has nothing to absorb cap-exceeded requests with. Surface a small
  // inline note above the Balance row + lean on the existing primary
  // "Buy Extra Usage" button (already prominent / blue).
  const needsBalance = enabled && balanceCents <= 0;

  return (
    <div className="mt-4 border-t border-white/10 pt-4">
      <div className="rounded-sm border border-white/10 bg-black/20 p-4">
        {/* Title row + toggle. ALWAYS rendered, even when toggle is off,
            so the owner has a stable place to enable Extra Usage. */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="font-title text-base font-semibold text-white">
              Extra Usage
            </h4>
            <p className="font-secondary mt-1 text-xs leading-relaxed text-white/55">
              Turn on extra usage to keep going if you hit the limit
            </p>
          </div>
          <div
            title={
              !isOwner
                ? "Only the organization owner can manage credits"
                : enabled
                  ? "Extra usage is on"
                  : "Extra usage is off"
            }
          >
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              disabled={!isOwner || saving || walletLoading}
              onClick={() => onToggleExtraUsage?.(!enabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                enabled ? "bg-primary" : "bg-white/15"
              } ${
                !isOwner || saving || walletLoading
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  enabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Collapsed-off body: render only title + toggle + subtitle when
            the master switch is OFF. Spec: hide spent/limit/balance/Adjust/
            Buy block entirely. Toggling on reveals the full controls
            via the next render — no animation lib is currently a dep
            for this file, so we render-or-omit. */}
        {enabled ? (
          <>
            {/* Spent / progress / reset */}
            <div className="mt-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-title text-lg font-semibold text-white">
                  {dollarsFromCents(spentCents)}{" "}
                  <span className="font-secondary text-xs font-normal text-white/45">
                    spent
                  </span>
                </p>
                <p className="font-secondary text-xs text-white/60">
                  {pct}% used
                </p>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/[0.08]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: pct >= 100 ? "#FF6B6B" : "#49B7F8",
                  }}
                />
              </div>
              <p className="font-secondary mt-2 text-[11px] text-white/40">
                Resets {formatResetDate(resetsAt)}
              </p>
            </div>

            {/* Full-node vs archival breakdown — requests the EXTRA USAGE
                wallet covered this month. Distinct from the plan's own
                Full node / Archive node cards above. */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-sm bg-black/20 px-3 py-2">
                <p className="font-secondary text-[11px] text-white/50">
                  Full node requests
                </p>
                <p className="font-title text-sm font-semibold text-white">
                  {formatInt(walletFullReq)}
                </p>
              </div>
              <div className="rounded-sm bg-black/20 px-3 py-2">
                <p className="font-secondary text-[11px] text-white/50">
                  Archival requests
                </p>
                <p className="font-title text-sm font-semibold text-white">
                  {formatInt(walletArchiveReq)}
                </p>
              </div>
            </div>

            {/* Two rows: monthly cap + balance */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between gap-3 rounded-sm bg-black/20 px-3 py-2">
                <div>
                  <p className="font-title text-sm font-semibold text-white">
                    {dollarsFromCents(capCents)}
                  </p>
                  <p className="font-secondary text-[11px] text-white/50">
                    Monthly spend limit
                  </p>
                </div>
                {isOwner ? (
                  <button
                    type="button"
                    onClick={onOpenAdjust}
                    disabled={saving || walletLoading}
                    className="font-secondary rounded-sm border border-white/15 bg-transparent px-3 py-1.5 text-xs font-semibold text-white/85 transition-colors hover:bg-white/[0.06] disabled:opacity-50"
                  >
                    Adjust Limit
                  </button>
                ) : (
                  <span
                    className="font-secondary rounded-sm border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] text-white/55"
                    title="Only the organization owner can manage credits"
                  >
                    Owner-only
                  </span>
                )}
              </div>
              {needsBalance ? (
                <p className="font-secondary px-1 text-[11px] text-amber-300/90">
                  Add credit to enable extra usage at the cap.
                </p>
              ) : null}
              <div className="flex items-center justify-between gap-3 rounded-sm bg-black/20 px-3 py-2">
                <div>
                  <p className="font-title text-sm font-semibold text-white">
                    {dollarsFromCents(balanceCents)}
                  </p>
                  <p className="font-secondary text-[11px] text-white/50">
                    Current Balance
                  </p>
                </div>
                {isOwner ? (
                  <button
                    type="button"
                    onClick={onOpenBuy}
                    disabled={saving || walletLoading}
                    className="font-secondary rounded-sm bg-primary px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-primary-hover disabled:opacity-50"
                  >
                    Buy Extra Usage
                  </button>
                ) : (
                  <span
                    className="font-secondary rounded-sm border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] text-white/55"
                    title="Only the organization owner can manage credits"
                  >
                    Owner-only
                  </span>
                )}
              </div>
            </div>

            {walletLoading ? (
              <p className="font-secondary mt-3 text-[11px] text-white/40">
                Loading…
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* SmallModal — shared shape for Adjust Limit + Buy Extra Usage.       */
/* Plain position:fixed overlay (no framer-motion dependency).         */
/* ------------------------------------------------------------------ */

function SmallModal({
  title,
  inputLabel,
  min,
  max,
  value,
  onChange,
  submitLabel,
  disabled,
  onCancel,
  onSubmit,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-md border border-white/10 bg-landing-section p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-title text-base font-semibold text-white">
          {title}
        </h3>
        <div className="mt-4">
          <label className="font-secondary text-xs text-white/60">
            {inputLabel} (whole dollars, ${min}–${max})
          </label>
          <div className="mt-2 flex items-center rounded-sm border border-white/15 bg-black/30 px-3 py-2">
            <span className="font-secondary text-sm text-white/55">$</span>
            <input
              type="number"
              inputMode="numeric"
              min={min}
              max={max}
              step="1"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              disabled={disabled}
              className="font-secondary ml-2 w-full bg-transparent text-sm text-white outline-none"
            />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className="font-secondary rounded-sm px-3 py-2 text-sm text-white/60 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={disabled}
            className="font-secondary rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-primary-hover disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
